import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { fileToBase64 } from '../../transforms/fileToBase64';
import type {
  NotificationTestFormValues,
  SelectedFile,
} from '../../types/notificationTest';

const CID_PATTERN = /^[A-Za-z0-9_\-]+$/;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

export interface InsertedImage {
  src: string;         
  cid?: string;        
  alt?: string;
  width?: number;      
  height?: number;     
}

const readImageDimensions = (
  file: File,
): Promise<{ width: number; height: number } | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });

interface InsertImageDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (image: InsertedImage) => void;
}

export const InsertImageDialog: React.FC<InsertImageDialogProps> = ({
  open,
  onClose,
  onInsert,
}) => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext<NotificationTestFormValues>();

  const [file, setFile] = useState<File | null>(null);
  const [inline, setInline] = useState(true);
  const [cid, setCid] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [fileError, setFileError] = useState<string | undefined>();
  const [cidError, setCidError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const resetState = (): void => {
    setFile(null);
    setInline(true);
    setCid('');
    setWidth('');
    setHeight('');
    setFileError(undefined);
    setCidError(undefined);
  };

  const handleFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setFile(null);
      setFileError(translate('resources.test.attachments.insertImage.errorNotImage'));
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFile(null);
      setFileError(translate('resources.test.attachments.insertImage.errorTooLarge'));
      return;
    }
    setFileError(undefined);
    setFile(f);

    const dims = await readImageDimensions(f);
    if (dims) {
      setWidth(String(dims.width));
      setHeight(String(dims.height));
    }
  };

  const parseDim = (s: string): number | undefined => {
    const trimmed = s.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
  };

  const validateCid = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return translate('resources.test.validation.contentIdRequired');
    if (!CID_PATTERN.test(trimmed))
      return translate('resources.test.validation.contentIdInvalidChars');
    const existing = getValues('attachments') ?? [];
    const duplicate = existing.some(
      (a) => a.inline === true && (a.contentId ?? '').trim() === trimmed,
    );
    if (duplicate) return translate('resources.test.validation.contentIdDuplicate');
    return undefined;
  };

  const handleInsert = async (): Promise<void> => {
    if (!file) {
      setFileError(translate('resources.test.attachments.insertImage.errorFileRequired'));
      return;
    }
    let cidErr: string | undefined;
    if (inline) {
      cidErr = validateCid(cid);
      setCidError(cidErr);
      if (cidErr) return;
    }

    setSubmitting(true);
    try {
      const base64 = await fileToBase64(file);
      const trimmedCid = cid.trim();

      const newAttachment: SelectedFile = inline
        ? {
            name: file.name,
            type: file.type,
            base64,
            inline: true,
            contentId: trimmedCid,
          }
        : {
            name: file.name,
            type: file.type,
            base64,
            inline: false,
            contentId: '',
          };

      const current = getValues('attachments') ?? [];
      setValue('attachments', [...current, newAttachment], {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (inline) {
        const blobUrl = URL.createObjectURL(file);
        onInsert({
          src: blobUrl,
          cid: trimmedCid,
          alt: trimmedCid,
          width: parseDim(width),
          height: parseDim(height),
        });
      }

      resetState();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {translate('resources.test.attachments.insertImage.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            type="file"
            inputProps={{ accept: 'image/*' }}
            onChange={handleFile}
            label={translate('resources.test.attachments.insertImage.fileLabel')}
            InputLabelProps={{ shrink: true }}
            error={Boolean(fileError)}
            helperText={
              fileError ??
              (file
                ? `File size: ${(file.size / 1024).toFixed(1)} KB`
                : translate('resources.test.attachments.insertImage.fileHelper'))
            }
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label={translate('resources.test.attachments.insertImage.width')}
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              placeholder="auto"
            />
            <TextField
              label={translate('resources.test.attachments.insertImage.height')}
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              placeholder="auto"
            />
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={inline}
                onChange={(e) => {
                  setInline(e.target.checked);
                  if (!e.target.checked) setCidError(undefined);
                }}
              />
            }
            label={translate('resources.test.attachments.embedInline')}
          />

          {inline && (
            <TextField
              label={translate('resources.test.attachments.contentId')}
              value={cid}
              onChange={(e) => {
                setCid(e.target.value);
                if (cidError) setCidError(undefined);
              }}
              required
              fullWidth
              placeholder="LOGO"
              InputLabelProps={{ shrink: true }}
              error={Boolean(cidError)}
              helperText={cidError}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {translate('resources.test.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleInsert}
          disabled={submitting || !file}
        >
          {translate('resources.test.attachments.insertImage.insertButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InsertImageDialog;
